import {
  recipeFilterUserPath,
  importPreviewSupplementLabelPath,
  importFromRecipeSupplementLabelPath,
  editSupplementLabelPath,
  supplementLabelPath,
} from "~/routes";
import { fetchJson, requestJson, submitForm } from "recipal-app/controllers/shared/requests";

import { debugLog } from "../supplement-editor/utils/logger";

function extractErrorMessage(data, fallback) {
  if (!data?.errors) return fallback;
  if (Array.isArray(data.errors)) return data.errors.join(", ");
  if (typeof data.errors === "string") return data.errors;
  return Object.values(data.errors).flat().join(", ");
}

function handleResponse({ response, data }, errorMessage = "Request failed") {
  if (!response.ok) {
    throw new Error(extractErrorMessage(data, errorMessage));
  }

  return data;
}

export const SupplementApi = {
  async searchRecipes(userId, query, { perPage = 20, signal } = {}) {
    debugLog("[SupplementApi] searchRecipes", { userId, query, perPage });
    const params = new URLSearchParams();
    if (query) params.append("q", query);
    params.append("per_page", perPage);

    const result = await submitForm(recipeFilterUserPath(userId), params, {
      signal,
    });

    const data = handleResponse(result, "Failed to search recipes");
    return data.recipes || data;
  },

  async previewImportFromRecipe(labelId, recipeId, { signal } = {}) {
    debugLog("[SupplementApi] previewImportFromRecipe", { labelId, recipeId });
    const result = await fetchJson(
      importPreviewSupplementLabelPath(labelId, { recipe_id: recipeId }),
      {
        signal,
      }
    );

    return handleResponse(result, "Failed to load preview");
  },

  async importFromRecipe(labelId, recipeId, categories = null, { signal } = {}) {
    debugLog("[SupplementApi] importFromRecipe", { labelId, recipeId, categories });
    const body = { recipe_id: recipeId };
    if (categories) body.categories = categories;

    const result = await requestJson(importFromRecipeSupplementLabelPath(labelId), {
      method: "POST",
      body: JSON.stringify(body),
      signal,
    });
    return handleResponse(result, "Import failed");
  },

  async saveLabel(labelId, payload, { signal } = {}) {
    debugLog("[SupplementApi] saveLabel", { labelId, payload });
    return requestJson(supplementLabelPath(labelId), {
      method: "PATCH",
      body: JSON.stringify(payload),
      signal,
    });
  },

  async fetchLabel(labelId, { signal } = {}) {
    debugLog("[SupplementApi] fetchLabel", { labelId });
    return fetchJson(supplementLabelPath(labelId), {
      signal,
    });
  },

  async pollLabelVersion(labelId, { signal } = {}) {
    debugLog("[SupplementApi] pollLabelVersion", { labelId });
    return fetchJson(`${supplementLabelPath(labelId)}/poll`, {
      signal,
    });
  },

  async reloadLabel(labelId) {
    debugLog("[SupplementApi] reloadLabel", { labelId });
    const result = await fetchJson(editSupplementLabelPath(labelId, { format: "json" }));
    return handleResponse(result, "Failed to reload label");
  },
};
